import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import { normalizeWidgetTheme, APIURL, getCSSStyles, getEChartsTooltipTheme, getWidgetDataURI, apiGet, toggleWidgetTheme, getSymbolOptions, getTraderSMCardHTML, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML, searchSymbols } from '../../helpers';

export class TraderversePriceTargetGapVisualizer {
 private renderSelectedSymbols(): void {
  const container = this.shadowRoot.getElementById('selected-symbols');
  if (!container) return;

  const symbolOptions = (window as any).getSymbolOptions
    ? (window as any).getSymbolOptions()
    : getSymbolOptions();

  const hasSymbols = !!(this.selectedSymbols && this.selectedSymbols.length > 0);

  container.innerHTML = this.selectedSymbols.map(symbol => {
    const symInfo =
      symbolOptions.find((s: any) => s.symbol === symbol) || { symbol };

    const logoImg =
      symInfo.type === 'crypto'
        ? `<img data-testing-id="symbol-logo-${symbol}" 
            src="https://media.traderverse.io/assets/crypto/${symbol.toUpperCase()}_CRYPTO.svg"
            onerror="this.onerror=null;this.src='https://cdn-images.traderverse.io/crypto_dummy.svg';" 
            alt="${symbol}">`
        : `<img data-testing-id="symbol-logo-${symbol}" 
            src="https://imagedelivery.net/09I_Y_JIwzvjUiGrV3dv3Q/stocks_v1/${symbol}.svg/public" 
            alt="${symbol}">`;

    return `<figure data-testing-id="symbol-item-${symbol}">
              ${logoImg}
            </figure>`;
  }).join('') +
    (hasSymbols
      ? `<svg data-testing-id="symbol-dropdown-arrow"
          width="11" height="6" viewBox="0 0 11 6" fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <path d="M0.706223 0.706055L5.41424 4.47247L10.1223 0.706055"
          stroke="#9A95AC"
          stroke-width="1.41241"
          stroke-linecap="round"
          stroke-linejoin="round"/>
        </svg>`
      : '');

  // ✅ IMPORTANT: AFTER render (fixes testing tool issue)
  if (hasSymbols) {
    container.setAttribute('data-testing-id', 'price-target-gap-visualizer');
  } else {
    container.removeAttribute('data-testing-id');
  }
}
  private container: HTMLElement;
  private config: WidgetConfig;
  private shadowRoot: ShadowRoot;
  private chartInstance: any = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private currentData: any = null;
  private selectedSymbols: string[] = ['AAPL', 'MSFT'];
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();


  constructor(config: WidgetConfig) {
    this.config = { theme: 'default-dark', ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.handleResize();
    });

    this.widgetResizeObserver = new ResizeObserver(() => {
      if (this.chartInstance) this.chartInstance.resize();
    });
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    this.createWidget();
    setupHeaderPin(this.shadowRoot, this.config);
    await this.injectChartLibrary();
    await this.attachStyles();
    this.setupChart();
    this.updateDisplay();

    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) this.widgetResizeObserver?.observe(widget);
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
        setTimeout(() => {
          const filterIds = [
            'p_gap_range_min',
            'p_gap_range_max',
            'p_sort_by',
            'p_show_only',
            'p_within_percent',
            'p_min_gap_threshold',
            'p_top_n'
          ];
          filterIds.forEach(id => {
            const el = this.shadowRoot.getElementById(id);
            if (el) {
              el.addEventListener('change', () => {
                this.setupChart();
              });
            }
          });
        }, 0);
    this.shadowRoot.innerHTML = `
      <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title market">${this.getTitleIconSvg()} Price Target Gap Visualizer
          </h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
        </div>

        <div class="inBody">
          <div class="optionsRow">
            ${this.config.hideSymbol ? '<div class="item dropStyle02"></div>' : `<div class="item dropStyle02">
              <span id="symbol-dropdown-placeholder"></span>
              <div id="selected-symbols" class="symbolsCol"></div>
            </div>`}
            <span></span>
            <div class="filtersBox sideBar hidden">
              <div class="item"><label>Min Gap %</label><input type="number" id="p_gap_range_min" value="-50" /></div>
              <div class="item"><label>Max Gap %</label><input type="number" id="p_gap_range_max" value="100" /></div>
              <div class="item"><label>Sort By</label><select id="p_sort_by"><option value="gap_percent">gap_percent</option><option value="symbol">symbol</option><option value="target_price">target_price</option></select></div>
              <div class="item"><label>Show Only</label><select id="p_show_only"><option value="all">all</option><option value="above_target">above_target</option><option value="below_target">below_target</option></select></div>
              <div class="item"><label>Within %</label><input type="number" id="p_within_percent" value="5" /></div>
              <div class="item"><label>Min Gap Threshold</label><input type="number" id="p_min_gap_threshold" value="10" /></div>
              <div class="item"><label>Top N</label><input type="number" id="p_top_n" value="20" /></div>
            </div>
          </div>

          <div class="chartDv" id="relativeValuationBarChartDv" style="height:320px;"></div>
        </div>
      </div>`;

    // Filters toggle logic
    const filtersBox = this.shadowRoot.querySelector('.filtersBox');
    const filtersBtn = this.shadowRoot.querySelector('.filters-toggle');
    if (filtersBtn && filtersBox) {
      filtersBtn.addEventListener('click', (ev) => {
        ev.stopPropagation();
        filtersBox.classList.toggle('hidden');
      });
    }

    // Add symbol dropdown logic (multi-select)
    if (this.config.hideSymbol) { /* skip */ } else {
    const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
    if (placeholder) {
      const dropdownContainerOutter = document.createElement('div');
      dropdownContainerOutter.className = 'itemInner';
      const dropdownContainer = document.createElement('div');
      dropdownContainer.className = 'symbol-dropdown-container';

      const input = document.createElement('input');
      input.type = 'text';
      input.className = 'symbol-search-input';
      input.placeholder = 'Search symbol...';
      const dropdownList = document.createElement('div');
      dropdownList.className = 'symbol-dropdown-list-multiple';

      const getAllOptions = () => ((window as any).getSymbolOptions ? (window as any).getSymbolOptions() : getSymbolOptions());

      let timeout = null as any;
      let requestId = 0;
      const renderDropdown = (filter = '') => {
        clearTimeout(timeout);
        timeout = setTimeout(async () => {
        const id = ++requestId;
        dropdownList.innerHTML = '<div style="padding:8px 12px;color:#888;">Searching...</div>';
        const keyword = filter.trim();
        let options = await searchSymbols({ keyword, market: 'stock' });
        if (id !== requestId) return;
        if (!keyword) {
          const selectedMissing = this.selectedSymbols
            .filter((s) => !options.some((item: any) => item.symbol === s))
            .map((s) => getAllOptions().find((item: any) => item.symbol === s) || { symbol: s, name: s, type: 'stock' });
          options = [...selectedMissing, ...options];
        }
        const selected = options.filter((opt: any) => this.selectedSymbols.includes(opt.symbol));
        const unselected = options.filter((opt: any) => !this.selectedSymbols.includes(opt.symbol));
        const sorted = [...selected, ...unselected];
        dropdownList.innerHTML = '';
        if (sorted.length === 0) {
          dropdownList.innerHTML = '<div style="padding:8px 12px;color:#888;">No results</div>';
          return;
        }
        sorted.forEach(opt => {
          const divOutter = document.createElement('div');
          divOutter.className = 'ditem';
          const div = document.createElement('div');
          div.className = 'trader-sm-card';
          if (this.selectedSymbols.includes(opt.symbol)) {
            divOutter.classList.add('selected-symbol-item');
          }
          div.innerHTML = getTraderSMCardHTML(opt);
          divOutter.innerHTML = '<div class="checkIcon"><i><svg width="7" height="5" viewBox="0 0 7 5" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M0.5 2.5L2.42929 4.42929C2.46834 4.46834 2.53166 4.46834 2.57071 4.42929L6.5 0.5" stroke="white" stroke-linecap="round"/> </svg> </i></div><div class="trader-sm-card">' + div.innerHTML + '</div>';
          divOutter.onclick = (e) => {
            e.stopPropagation();
            if (this.selectedSymbols.includes(opt.symbol)) {
              this.selectedSymbols = this.selectedSymbols.filter(s => s !== opt.symbol);
            } else {
              this.selectedSymbols.push(opt.symbol);
            }
            renderDropdown(input.value);
            this.renderSelectedSymbols();
            this.setupChart();
          };
          dropdownList.appendChild(divOutter);
        });
        }, 300);
      };

      input.addEventListener('focus', () => {
        dropdownList.style.display = 'block';
        renderDropdown(input.value);
      });
      input.addEventListener('input', () => {
        dropdownList.style.display = 'block';
        renderDropdown(input.value);
      });
      input.addEventListener('blur', () => {
        setTimeout(() => dropdownList.style.display = 'none', 150);
      });

      dropdownContainer.appendChild(input);
      dropdownContainer.appendChild(dropdownList);
      dropdownContainerOutter.appendChild(dropdownContainer);
      placeholder.replaceWith(dropdownContainerOutter);

      // Initial render
      renderDropdown();
    }

    this.renderSelectedSymbols();
    }
  }

  private async injectChartLibrary(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      if ((window as any).echarts) return resolve(true);

      if (this.shadowRoot.querySelector('script[data-chart-lib]')) {
        const script = this.shadowRoot.querySelector('script[data-chart-lib]') as HTMLScriptElement;
        script.onload = () => resolve(true);
      } else {
        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
        script.async = true;
        script.setAttribute('data-chart-lib', 'true');
        this.shadowRoot.appendChild(script);
        script.onload = () => resolve(true);
      }
    });
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

      private async setupChart(): Promise<void> {
          const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
          if (widget) widget.classList.add('loading');
          try {
              const params: Record<string, string> = {};
              const getValue = (id: string, def: any) => {
              const el = this.shadowRoot.getElementById(id) as HTMLInputElement | HTMLSelectElement;
              return el ? el.value : def;
              };
              params.p_gap_range_min = getValue('p_gap_range_min', '-50');
              params.p_gap_range_max = getValue('p_gap_range_max', '100');
              params.p_sort_by = getValue('p_sort_by', 'gap_percent');
              params.p_show_only = getValue('p_show_only', 'all');
              params.p_within_percent = getValue('p_within_percent', '5');
              params.p_min_gap_threshold = getValue('p_min_gap_threshold', '10');
              params.p_top_n = getValue('p_top_n', '20');
              // Symbols
              if (this.selectedSymbols.length > 0) {
                const symbolParams = this.selectedSymbols.map(s => `p_symbols=${encodeURIComponent(s)}`).join('&');
                params._symbolsQuery = symbolParams; // Store separately for later
              }
              const qsParts = Object.keys(params)
                .filter(k => k !== '_symbolsQuery')
                .map(k => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`);
              if (params._symbolsQuery) qsParts.push(params._symbolsQuery);
              const qs = qsParts.join('&');
              const url = `${APIURL}/v1/discover/price-target-gap?${qs}`;

                  const response = await apiGet({
                    method: 'GET',
                    url,
                    headers: { 'Content-Type': 'application/json' }
                  });

                  if (!response.ok) throw new Error("Failed to fetch data");
                  const data = await response.json();
                  const items = Array.isArray(data) ? data : (data && (data.data || data.items || data.results)) || [];
                  this.currentData = items;
                  this.updateDisplay();
                  this.emit('dataUpdate', this.currentData);
          } catch (err) {
              console.error("Error fetching chart data:", err);
              this.currentData = [];
              this.updateDisplay();
          } finally {
              if (widget) widget.classList.remove('loading');
          }
      }
  

  private updateDisplay(): void {
    if (!(window as any).echarts) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderChart();
  }

  private renderChart(): void {
    const echarts = (window as any).echarts;
    const container = this.shadowRoot.querySelector('#relativeValuationBarChartDv') as HTMLElement;
    console.log('Rendering chart in container:', this.currentData);
    if (!container) return;

    let items: any[] = [];
    if (this.currentData && Array.isArray(this.currentData.data)) {
      items = this.currentData.data;
    } else if (Array.isArray(this.currentData)) {
      items = this.currentData;
    }

    const hasData = Array.isArray(items) && items.length > 0;

    const existing = echarts.getInstanceByDom(container);
    if (existing) echarts.dispose(existing);

    if (!hasData) {
      container.removeAttribute('data-testing-id');
      container.innerHTML = getNoDataHTML();
      this.chartInstance = null;
      return;
    }

    container.setAttribute('data-testing-id', 'price-target-gap-chart');
    this.chartInstance = echarts.init(container);

    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
    const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
    const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');

    items.sort((a, b) => Math.abs(b.gap_percent) - Math.abs(a.gap_percent));
    const yLabels = items.map(item => item.symbol || '');
    const positiveData = items.map(item => item.gap_percent > 0 ? item.gap_percent : 0);
    const negativeData = items.map(item => item.gap_percent < 0 ? item.gap_percent : 0);

    // Reserve enough left space so symbol names are not clipped
    const longestLabelChars = yLabels.reduce((max: number, label: string) => {
      return Math.max(max, String(label || '').length);
    }, 0);
    const gridLeft = Math.min(Math.max(Math.ceil(longestLabelChars * 7.5) + 16, 56), 120);

    const option = {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...getEChartsTooltipTheme(widgetBox),
        formatter: (params: any) => {
          const tipItems = Array.isArray(params) ? params : [params];
          const index = tipItems[0]?.dataIndex ?? 0;
          const row = items[index];
          if (!row) return '';
          const gap = Number(row.gap_percent ?? 0);
          const color = gap >= 0 ? '#3CF26E' : '#FF4A4A';
          const formatPrice = (v: any) =>
            v != null && !Number.isNaN(Number(v))
              ? `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`
              : '—';
          return `
            <div style="min-width:140px">
              <div style="font-weight:600;margin-bottom:6px">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px"></span>
                ${row.symbol || '—'}
              </div>
              <div>Gap: <b>${gap.toFixed(2)}%</b></div>
              <div>Current: <b>${formatPrice(row.current_price ?? row.price)}</b></div>
              <div>Target: <b>${formatPrice(row.target_price)}</b></div>
            </div>
          `;
        }
      },
      grid: { left: gridLeft, right: 16, top: 8, bottom: 28, containLabel: false },
      xAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: axisLineColor } },
        axisTick: { show: false },
        splitLine: { show: true, lineStyle: { type: 'dotted', color: splitLineColor }},
        axisLabel: { color: textColor }
      },
      yAxis: {
        type: 'category',
        data: yLabels,
        axisLine: { lineStyle: { color: axisLineColor } },
        axisTick: { show: false },
        axisLabel: {
          color: textColor,
          fontSize: 12,
          interval: 0,
          margin: 8
        }
      },
      series: [
        {
          name: 'positive',
          type: 'bar',
          data: positiveData,
          barWidth: '60%',
          itemStyle: {
            color: '#3CF26E',
            borderRadius: [0, 6, 6, 0]
          }
        },
        {
          name: 'negative',
          type: 'bar',
          data: negativeData,
          barWidth: '60%',
          itemStyle: {
            color: '#FF4A4A',
            borderRadius: [6, 0, 0, 6]
          }
        }
      ]
    };

    this.chartInstance.setOption(option);
  }

      private handleResize(): void {
      if (this.currentData && Array.isArray(this.currentData)) {
        try {
          this.currentData.forEach(chart => {
            if (chart) chart.resize();
          });
        } catch (err) {
          console.error('Error resizing chart:', err);
        }
      }
    }
  
  
    public on(type: WidgetEventType, handler: WidgetEventHandler): void {
        if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
        this.eventHandlers.get(type)!.push(handler);
      }
    
      public off(type: WidgetEventType, handler: WidgetEventHandler): void {
        const list = this.eventHandlers.get(type);
        if (list) this.eventHandlers.set(type, list.filter(h => h !== handler));
      }
    
      public async getWidgetImage(): Promise<string> {
        const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
        return await getWidgetDataURI(widget);
      }
    
  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    this.updateDisplay();
  }
  

  public destroy(): void {
    if (this.widgetResizeObserver) this.widgetResizeObserver.disconnect();
    if (this.chartInstance) this.chartInstance.dispose();
    this.shadowRoot.innerHTML = '';
  }

      private emit(type: WidgetEventType, data: any): void {
          const handlers = this.eventHandlers.get(type);
          if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach(h => h(evt)); }
      }
    private getTitleIconSvg(): string {
      return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/>
            <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/>
          </svg>`;
    }

}

declare global {
  interface Window {
    TraderversePriceTargetGapVisualizer: typeof TraderversePriceTargetGapVisualizer;
  }
}
if (typeof window !== 'undefined') {
  window.TraderversePriceTargetGapVisualizer = TraderversePriceTargetGapVisualizer;
}